import { useId, useState, type FormEvent } from 'react';
import type { AdminUser } from '@shared/api';
import { LIMITS } from '@shared/constants';
import { errorMessage, post } from '../lib/api';
import { Dialog } from './Dialog';
import { Notice } from './Notice';

/** Readable random password (no 0/O, 1/l/I). */
function generatePassword(length = 12): string {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint32Array(length));
  return Array.from(bytes, (n) => chars[n % chars.length]).join('');
}

interface Props {
  open: boolean;
  onClose(): void;
  onCreated(user: AdminUser): void;
}

/** Administrator creates a test account: full access with no billing; credentials shown once to be shared. */
export function TestUserDialog({ open, onClose, onCreated }: Props) {
  const formId = useId();
  const ids = { name: useId(), email: useId(), password: useId() };
  const [form, setForm] = useState({ name: '', email: '', password: generatePassword() });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  function close() {
    if (busy) return;
    setForm({ name: '', email: '', password: generatePassword() });
    setError(null);
    setCreated(null);
    setCopied(false);
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await post<{ user: AdminUser }>('/api/admin/users', form);
      setCreated({ email: user.email, password: form.password });
      onCreated(user);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const accessText = created
    ? `Voz Livre App\nEndereço: ${window.location.origin}\nE-mail: ${created.email}\nSenha: ${created.password}`
    : '';

  async function copy() {
    try {
      await navigator.clipboard.writeText(accessText);
      setCopied(true);
    } catch {
      window.prompt('Copie os dados de acesso:', accessText);
    }
  }

  const set = (key: 'name' | 'email' | 'password') => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Dialog
      open={open}
      title="Usuário de teste"
      onClose={close}
      dismissible={!busy}
      footer={
        created ? (
          <button type="button" className="btn btn--primary" onClick={close}>
            Concluir
          </button>
        ) : (
          <>
            <button type="button" className="btn btn--ghost" onClick={close} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" form={formId} className="btn btn--primary" disabled={busy}>
              {busy ? 'Criando…' : 'Criar usuário'}
            </button>
          </>
        )
      }
    >
      {created ? (
        <div className="form">
          <Notice kind="success">Usuário criado. Envie os dados de acesso para a pessoa.</Notice>
          <pre className="access-box">{accessText}</pre>
          <button type="button" className="btn btn--secondary" onClick={() => void copy()}>
            {copied ? '✓ Copiado' : 'Copiar dados de acesso'}
          </button>
          <p className="muted small">Copie agora: a senha não aparece de novo depois de fechar. Se ela se perder, exclua o usuário e crie outro.</p>
        </div>
      ) : (
        <form id={formId} className="form" onSubmit={submit} noValidate>
          <p className="muted small">Acesso completo, sem cartão, sem cobrança e sem prazo, para demonstrações e pessoas que vão testar o app. Ela aparece na lista com a marca “teste”. Quando quiser, use “Migrar para plano normal”: aí começam os 45 dias grátis e depois a cobrança.</p>
          <div className="field">
            <label htmlFor={ids.name}>Nome</label>
            <input id={ids.name} required maxLength={100} value={form.name} onChange={set('name')} />
          </div>
          <div className="field">
            <label htmlFor={ids.email}>E-mail</label>
            <input id={ids.email} type="email" required autoComplete="off" value={form.email} onChange={set('email')} />
          </div>
          <div className="field">
            <label htmlFor={ids.password}>Senha</label>
            <div className="input-row">
              <input
                id={ids.password}
                required
                autoComplete="off"
                minLength={LIMITS.minPasswordLength}
                value={form.password}
                onChange={set('password')}
              />
              <button type="button" className="btn btn--ghost btn--small" onClick={() => setForm((f) => ({ ...f, password: generatePassword() }))}>
                Gerar outra
              </button>
            </div>
          </div>
          {error && <Notice kind="error">{error}</Notice>}
        </form>
      )}
    </Dialog>
  );
}
